import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { AppButton } from "../../../shared/components/ui";
import { useFieldHeader, useFieldJourney } from "../../../shared/components/FieldShell";
import { IconCheck, IconError, IconInfo, IconSpinner } from "../../../shared/components/icons";
import { getPaymentTimeline } from "../api";
import { formatDate, formatTime, formatTzs } from "../format";
import type { PaymentTimelineEntry } from "../types";

/**
 * S5 — Customer payment timeline (handoff TASK-009, s5-customer-timeline-*).
 * Audit view the agent can show the customer: payment received → arrears
 * applied → token generated → SMS sent, plus the pending next-due entry.
 * Amounts render from integer cents; timestamps are server UTC shown local.
 */

function entryLine(e: PaymentTimelineEntry): string {
  const parts = [
    e.at ? (e.type === "payment_due" ? formatDate(e.at) : `Today ${formatTime(e.at)}`) : null,
    e.meta,
    e.amount_cents !== null ? formatTzs(e.amount_cents) : null,
  ].filter(Boolean);
  return parts.join(" · ");
}

export function S5CustomerTimeline() {
  const { id = "" } = useParams();
  const customerId = Number(id);
  const [shareState, setShareState] = useState<"idle" | "copied" | "failed">("idle");

  const query = useQuery({
    queryKey: ["payment-timeline", customerId],
    queryFn: () => getPaymentTimeline(customerId),
    retry: 1,
    staleTime: 15_000,
  });

  const data = query.data;
  useFieldHeader(
    "Payment timeline",
    data
      ? `${data.customer.name}${data.customer.contract_number ? ` — ${data.customer.contract_number}` : ""}`
      : undefined,
  );
  useFieldJourney({ flow: "payments", step: 4, backTo: "/field/payments/new" });

  if (query.isPending) {
    return (
      <div className="pg-state">
        <span className="state-icon info">
          <IconSpinner size={40} />
        </span>
        <h2>Loading timeline…</h2>
      </div>
    );
  }

  if (query.isError || !data) {
    return (
      <div className="pg-state">
        <span className="state-icon error">
          <IconError size={40} />
        </span>
        <h2>Could not load the timeline</h2>
        <p>{query.error instanceof Error ? query.error.message : "Something went wrong"}</p>
        <AppButton variant="secondary" type="button" onClick={() => void query.refetch()}>
          Retry
        </AppButton>
      </div>
    );
  }

  const share = async () => {
    const text = [
      `PAY-GO Africa — payment receipt for ${data.customer.name}`,
      ...data.entries
        .filter((e) => e.state === "done")
        .map((e) => `• ${e.title}: ${entryLine(e)}`),
    ].join("\n");
    if (navigator.share) {
      try {
        await navigator.share({ title: "PAY-GO payment receipt", text });
        return;
      } catch {
        /* user dismissed or share failed — fall through to clipboard */
      }
    }
    try {
      await navigator.clipboard.writeText(text);
      setShareState("copied");
    } catch {
      setShareState("failed");
    }
  };

  return (
    <>
      {data.entries.length === 0 ? (
        <div className="pg-state">
          <span className="state-icon info">
            <IconInfo size={40} />
          </span>
          <h2>No payments yet</h2>
          <p>Payments, allocations and tokens for this customer will appear here.</p>
        </div>
      ) : (
        <ul className="pg-timeline" aria-label="Payment audit timeline">
          {data.entries.map((e) => (
            <li key={e.id}>
              <span className={`dot ${e.state}`} aria-hidden="true">
                {e.state === "done" ? <IconCheck size={10} /> : null}
              </span>
              <div className={`t-title${e.state === "pending" ? " pending" : ""}`}>{e.title}</div>
              <div className="t-meta">{entryLine(e)}</div>
            </li>
          ))}
        </ul>
      )}

      {shareState === "copied" ? (
        <div className="pg-draft-note">
          <IconCheck size={14} /> Receipt copied — paste it into any message.
        </div>
      ) : null}
      {shareState === "failed" ? (
        <div className="pg-inline-error" role="alert">
          <IconError size={14} /> Sharing is not available on this device.
        </div>
      ) : null}

      <div className="pg-actions">
        <AppButton type="button" onClick={() => void share()}>
          Share receipt
        </AppButton>
      </div>
    </>
  );
}
